<script setup>
import EmptyState from '@/components/EmptyState';
import { ref, inject } from 'vue';
import { useDealershipStore } from '@/stores/dealership';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const dealershipStore = useDealershipStore();
const { search } = dealershipStore;
const { all } = storeToRefs(dealershipStore);
const { planOptions } = storeToRefs(useOptionsStore());

const params = ref({
    term: '',
    plan: null,
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchDealerships = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchDealerships();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchDealerships();
};

const openModal = (modal, mode = 'Create', dealership = null) => {
    modals.show(modal, {
        props: dealership ? {
            dealership: dealership,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchDealerships();
    }).catch(() => {
        // Modal cancelled
    });
};
</script>

<template>
<MainContentLayout>
    <template #title>{{ $t('label.dealership_management') }}</template>
    <template #actions>
        <StyledButton @click="openModal('DealershipsModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.new_dealership') }}
        </StyledButton>
    </template>
    <div class="rounded-xl border border-line bg-surface p-4 mb-4">
        <div class="grid lg:grid-cols-3 gap-x-3">
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchDealerships"
                name="search"
                :label="$t('label.search')"
                :debounce-events="['input']"
                @click:clear="clearSearch" />
            <StyledDropdown
                v-model="params.plan"
                clearable
                :placeholder="$t('label.plan')"
                :options="planOptions"
                name="plan"
                @input="searchDealerships" />
            <StyledDropdown
                v-model="params.status"
                clearable
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.active'), value: true },
                    { label: $t('label.inactive'), value: false },
                ]"
                name="status"
                @input="searchDealerships" />
        </div>
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'name', value: item => item.name, sortable: false },
            { title: $t('label.slug'), key: 'slug', value: item => item.slug, sortable: false },
            { title: $t('label.plan'), key: 'plan', value: item => item.plan_name ?? '', sortable: false },
            { title: $t('label.mode'), key: 'mode', value: item => item.is_white_label ? $t('label.white_label') : $t('label.shared'), sortable: false },
            { title: $t('label.status'), key: 'status', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: $t('title.edit'), icon: 'mdi-pencil-outline', action: (item) => openModal('DealershipsModal', 'Edit', item) },
        ]"
        @table-update="tableUpdate">
        <template #[`item.slug`]="{ item }">
            <span class="font-mono text-xs text-ink-light">{{ item.slug }}</span>
        </template>
        <template #[`item.mode`]="{ item }">
            <StatusChip :color="item.is_white_label ? 'accent' : 'neutral'">
                {{ item.is_white_label ? $t('label.white_label') : $t('label.shared') }}
            </StatusChip>
        </template>
        <template #[`item.status`]="{ item }">
            <StatusChip :color="item.is_active ? 'success' : 'neutral'">
                {{ item.is_active ? $t('label.active') : $t('label.inactive') }}
            </StatusChip>
        </template>
        <template #no-data>
            <EmptyState
                icon="mdi-office-building-outline"
                :title="$t('label.no_dealerships_found')"
                :hint="$t('label.no_dealerships_hint')">
                <StyledButton @click="openModal('DealershipsModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.new_dealership') }}
                </StyledButton>
            </EmptyState>
        </template>
    </DataTable>
</MainContentLayout>
</template>
